import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from 'framer-motion';
import icons from './shared/Icons';
import Section from './shared/Section';
import SectionHeading from './shared/SectionHeading';

/* ═══════════════════════════════════════════════
   FEATURE DATA
   ═══════════════════════════════════════════════ */
const FEATURES = [
  {
    icon: 'brain',
    title: 'Neural Analytics Engine',
    desc: 'Deep learning models that continuously evolve, identifying hidden patterns across petabytes of data.',
    detail: 'Built on transformer-based architectures, our engine auto-selects the optimal model for your data type—whether tabular, time-series, NLP, or image. It retrains itself on new data without manual intervention.',
    color: '#08CB00',
    image: process.env.PUBLIC_URL + '/features_result.webp',
  },
  {
    icon: 'chart',
    title: 'Predictive Forecasting',
    desc: 'ML-powered projections with 98%+ accuracy, enabling proactive decision-making months ahead.',
    detail: 'Combines ensemble methods (XGBoost, LSTM, Prophet) with real-time market signals. Confidence intervals and scenario simulations give you a full picture, not just a single number.',
    color: '#54E346',
    image: process.env.PUBLIC_URL + '/features (2)_result.webp',
  },
  {
    icon: 'shield',
    title: 'Enterprise Security',
    desc: 'SOC 2 Type II & ISO 27001 certified with end-to-end encryption and zero-trust architecture.',
    detail: 'Includes role-based access, field-level encryption, SIEM integration, and automated compliance reporting for GDPR, HIPAA, and CCPA. Pen-tested quarterly by independent auditors.',
    color: '#08CB00',
    image: process.env.PUBLIC_URL + '/features (3)_result.webp',
  },
  {
    icon: 'zap',
    title: 'Real-Time Processing',
    desc: 'Sub-millisecond latency on streaming data pipelines. Millions of events per second.',
    detail: 'Powered by Apache Kafka and our custom stream processor. Handles burst traffic with auto-scaling, back-pressure management, and exactly-once delivery guarantees.',
    color: '#54E346',
    image: process.env.PUBLIC_URL + '/features (4)_result.webp',
  },
  {
    icon: 'globe',
    title: 'Multi-Cloud Deploy',
    desc: 'Deploy on AWS, Azure, GCP, or on-premise with seamless hybrid architecture.',
    detail: 'Kubernetes-native with Helm charts for every major cloud. Our control plane provides a single dashboard across environments with cross-cloud failover and data locality compliance.',
    color: '#08CB00',
    image: process.env.PUBLIC_URL + '/features (5)_result.webp',
  },
  {
    icon: 'layers',
    title: 'Custom AI Models',
    desc: 'Build, train, and deploy bespoke models with our no-code ML studio in hours.',
    detail: 'Drag-and-drop feature engineering, automated hyperparameter tuning, A/B testing, and one-click deployment to REST endpoints. Supports PyTorch, TensorFlow, and ONNX exports.',
    color: '#54E346',
    image: process.env.PUBLIC_URL + '/features (6)_result.webp',
  },
];

const COUNT = FEATURES.length;
const ANGLE_STEP = (2 * Math.PI) / COUNT;

/* ═══════════════════════════════════════════════
   STARRY BACKGROUND — subtle dots
   ═══════════════════════════════════════════════ */
const STARS = Array.from({ length: 60 }, (_, i) => ({
  id: i,
  left: `${Math.random() * 100}%`,
  top: `${Math.random() * 100}%`,
  size: 1 + Math.random() * 2,
  dur: 2 + Math.random() * 4,
  delay: Math.random() * 3,
}));

const StarField = React.memo(() => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
    {STARS.map((s) => (
      <motion.div
        key={s.id}
        className="absolute rounded-full bg-primary/30"
        style={{ left: s.left, top: s.top, width: s.size, height: s.size }}
        animate={{ opacity: [0.15, 0.6, 0.15] }}
        transition={{ duration: s.dur, repeat: Infinity, delay: s.delay, ease: 'easeInOut' }}
      />
    ))}
    {/* Radial gradient overlay */}
    <div
      className="absolute inset-0"
      style={{ background: 'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(8,203,0,0.04) 0%, transparent 70%)' }}
    />
  </div>
));

/* ═══════════════════════════════════════════════
   AI CORE HUB — central element
   ═══════════════════════════════════════════════ */
const AICore = React.memo(() => (
  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
    {/* Outer pulse ring */}
    <motion.div
      className="absolute -inset-8 rounded-full border border-[#54E346]/20"
      animate={{ scale: [1, 1.35, 1], opacity: [0.4, 0, 0.4] }}
      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
    />
    <motion.div
      className="absolute -inset-4 rounded-full border border-[#54E346]/30"
      animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.1, 0.5] }}
      transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut', delay: 0.4 }}
    />
    {/* Core circle */}
    <div
      className="relative w-28 h-28 rounded-full flex items-center justify-center"
      style={{
        background: 'radial-gradient(circle, rgba(84,227,70,0.25) 0%, rgba(8,203,0,0.08) 60%, transparent 100%)',
        boxShadow: '0 0 40px rgba(84,227,70,0.25), 0 0 80px rgba(8,203,0,0.1)',
      }}
    >
      <div
        className="w-20 h-20 rounded-full flex flex-col items-center justify-center border border-[#54E346]/40"
        style={{
          background: 'radial-gradient(circle, rgba(84,227,70,0.15) 0%, rgba(0,0,0,0.8) 100%)',
          boxShadow: 'inset 0 0 20px rgba(84,227,70,0.15)',
        }}
      >
        <span className="text-[#54E346] text-xs font-mono font-bold tracking-widest">AI</span>
        <span className="text-[#54E346]/60 text-[9px] font-mono tracking-wider">CORE</span>
      </div>
    </div>
  </div>
));

/* ═══════════════════════════════════════════════
   ORBIT RING — decorative ellipse
   ═══════════════════════════════════════════════ */
const OrbitRing = React.memo(({ rx, ry }) => (
  <svg
    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
    width={rx * 2 + 40}
    height={ry * 2 + 40}
    viewBox={`0 0 ${rx * 2 + 40} ${ry * 2 + 40}`}
    aria-hidden="true"
  >
    <ellipse
      cx={rx + 20}
      cy={ry + 20}
      rx={rx}
      ry={ry}
      fill="none"
      stroke="#08CB00"
      strokeWidth="1"
      strokeOpacity="0.12"
      strokeDasharray="6 8"
    />
  </svg>
));

/* ═══════════════════════════════════════════════
   SATELLITE CARD (desktop orbit card)
   ═══════════════════════════════════════════════ */
const SatelliteCard = ({ feature, angle, rx, ry, onSelect }) => {
  const x = Math.cos(angle) * rx;
  const y = Math.sin(angle) * ry;
  /* Scale based on vertical position to create depth */
  const depth = 0.7 + 0.3 * ((Math.sin(angle) + 1) / 2);

  return (
    <motion.div
      className="absolute left-1/2 top-1/2 cursor-pointer group"
      style={{
        x: x - 120,
        y: y - 80,
        scale: depth,
        zIndex: Math.round(depth * 10),
      }}
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
      whileHover={{ scale: depth * 1.08 }}
      transition={{ type: 'spring', stiffness: 200, damping: 20 }}
    >
      <div
        className="w-[240px] rounded-xl p-6 border border-[#08CB00]/30 bg-[#0a0a0a]/90 backdrop-blur-sm
                   transition-shadow duration-300
                   group-hover:border-[#08CB00]/60 group-hover:shadow-[0_0_24px_rgba(8,203,0,0.2)]"
      >
        {/* Connection line to center */}
        <div
          className="absolute left-1/2 top-1/2 w-px origin-bottom-left pointer-events-none"
          style={{
            height: Math.sqrt(x * x + y * y),
            transform: `rotate(${Math.atan2(-y, -x) + Math.PI / 2}rad)`,
            background: 'linear-gradient(to top, rgba(8,203,0,0.12), transparent)',
          }}
          aria-hidden="true"
        />
        <div className="relative z-10">
          <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4
                          group-hover:bg-primary/20 transition-colors overflow-hidden">
            {feature.image ? (
              <div className="w-full h-full bg-center bg-cover rounded-lg" style={{ backgroundImage: `url("${encodeURI(feature.image)}")` }} />
            ) : (
              icons[feature.icon]
            )}
          </div>
          <h3 className="text-lg font-bold text-white mb-2 group-hover:text-primary transition-colors leading-tight">
            {feature.title}
          </h3>
          <p className="text-[15px] text-gray-400 leading-snug line-clamp-2 mb-3">{feature.desc}</p>
          <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-primary/70 group-hover:text-primary transition-colors">
            Learn More
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </span>
        </div>
      </div>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════
   DETAIL MODAL (desktop)
   ═══════════════════════════════════════════════ */
const DetailModal = ({ feature, onClose }) => (
  <motion.div
    className="fixed inset-0 z-50 flex items-center justify-center p-4"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    onClick={onClose}
  >
    {/* Backdrop */}
    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" aria-hidden="true" />

    <motion.div
      className="relative w-full max-w-lg rounded-2xl border border-[#08CB00]/30 bg-[#0a0a0a] p-6 sm:p-8 overflow-y-auto"
      initial={{ scale: 0.85, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.85, opacity: 0 }}
      transition={{ type: 'spring', stiffness: 260, damping: 22 }}
      onClick={(e) => e.stopPropagation()}
      style={{ maxHeight: '90vh', boxShadow: '0 0 60px rgba(8,203,0,0.12)' }}
    >
      {/* Glow blob */}
      <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-primary/5 blur-3xl pointer-events-none" aria-hidden="true" />

      {/* Close */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-8 h-8 rounded-full bg-dark-card border border-dark-border
                   flex items-center justify-center text-gray-400 hover:text-white transition-colors z-10"
        aria-label="Close"
      >
        {icons.close}
      </button>

      {/* Content */}
      <div className="relative z-10">
        <div
          className="w-14 h-14 rounded-xl flex items-center justify-center text-primary mb-5 overflow-hidden"
          style={{ background: `${feature.color}15`, boxShadow: `0 0 24px ${feature.color}22` }}
        >
          {feature.image ? (
            <div className="w-full h-full bg-center bg-contain bg-no-repeat" style={{ backgroundImage: `url("${encodeURI(feature.image)}")` }} />
          ) : (
            icons[feature.icon]
          )}
        </div>
        <h3 className="text-4xl font-bold mb-4">{feature.title}</h3>
        <p className="text-lg text-gray-400 leading-relaxed mb-5">{feature.desc}</p>

        {/* Detail */}
        <div className="border-t border-primary/10 pt-5">
          <p className="text-gray-300 text-lg leading-relaxed">{feature.detail}</p>
        </div>
      </div>
    </motion.div>
  </motion.div>
);

/* ═══════════════════════════════════════════════
   DESKTOP ORBITAL LAYOUT
   ═══════════════════════════════════════════════ */
const DesktopOrbit = () => {
  const [selected, setSelected] = useState(null);
  const [paused, setPaused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  /* Continuous rotation angle driven by framer-motion */
  const baseAngle = useMotionValue(0);

  /* Auto-rotate */
  useEffect(() => {
    if (paused) return;
    const controls = animate(baseAngle, baseAngle.get() + Math.PI * 2, {
      duration: 40,
      ease: 'linear',
      repeat: Infinity,
      repeatType: 'loop',
    });
    return () => controls.stop();
  }, [paused, baseAngle]);

  /* Drag to spin */
  const dragStartX = useRef(0);
  const dragStartAngle = useRef(0);

  const handlePointerDown = useCallback((e) => {
    if (e.target.closest('[data-card]')) return; // let card clicks through
    setPaused(true);
    setIsDragging(true);
    dragStartX.current = e.clientX;
    dragStartAngle.current = baseAngle.get();
  }, [baseAngle]);

  const handlePointerMove = useCallback((e) => {
    if (!isDragging) return;
    const dx = e.clientX - dragStartX.current;
    baseAngle.set(dragStartAngle.current + dx * 0.004);
  }, [isDragging, baseAngle]);

  const handlePointerUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      setPaused(false);
    }
  }, [isDragging]);

  const openModal = useCallback((idx) => {
    setPaused(true);
    setSelected(idx);
  }, []);

  const closeModal = useCallback(() => {
    setSelected(null);
    setPaused(false);
  }, []);

  /* Ellipse radii — responsive */
  const RX = 420;
  const RY = 240;

  /* Derive per-card angles from baseAngle */
  const angles = useTransform(baseAngle, (v) =>
    FEATURES.map((_, i) => v + i * ANGLE_STEP)
  );

  return (
    <>
      <div
        className="hidden lg:block relative mx-auto"
        style={{ width: RX * 2 + 280, height: RY * 2 + 280 }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      >
        <OrbitRing rx={RX} ry={RY} />
        <AICore />
        <OrbitCards angles={angles} rx={RX} ry={RY} onSelect={openModal} />
        {/* Drag hint */}
        <motion.p
          className="absolute bottom-0 left-1/2 -translate-x-1/2 text-[11px] text-primary/30 font-mono tracking-widest"
          animate={{ opacity: [0.3, 0.7, 0.3] }}
          transition={{ duration: 3, repeat: Infinity }}
        >
          drag to spin · click to explore
        </motion.p>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {selected !== null && (
          <DetailModal feature={FEATURES[selected]} onClose={closeModal} />
        )}
      </AnimatePresence>
    </>
  );
};

/* Extracted orbit cards to consume motion value */
const OrbitCards = ({ angles, rx, ry, onSelect }) => {
  const [cardAngles, setCardAngles] = useState(() => FEATURES.map((_, i) => i * ANGLE_STEP));

  useEffect(() => {
    const unsub = angles.on('change', (val) => setCardAngles(val));
    return unsub;
  }, [angles]);

  return (
    <>
      {FEATURES.map((f, i) => (
        <SatelliteCard
          key={f.title}
          feature={f}
          angle={cardAngles[i]}
          rx={rx}
          ry={ry}
          onSelect={() => onSelect(i)}
        />
      ))}
    </>
  );
};

/* ═══════════════════════════════════════════════
   MOBILE SWIPEABLE CARDS
   ═══════════════════════════════════════════════ */
const MobileFeatures = ({ onSelect }) => {
  const scrollRef = useRef(null);
  const [active, setActive] = useState(0);

  /* Auto-rotate on load */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const id = setInterval(() => {
      const next = (active + 1) % COUNT;
      const cardW = el.scrollWidth / COUNT;
      el.scrollTo({ left: cardW * next, behavior: 'smooth' });
    }, 4000);
    return () => clearInterval(id);
  }, [active]);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    const cardW = el.scrollWidth / COUNT;
    setActive(Math.round(el.scrollLeft / cardW));
  };

  return (
    <div className="lg:hidden">
      {/* Mini AI Core */}
      <div className="flex justify-center mb-8">
        <motion.div
          className="w-16 h-16 rounded-full flex items-center justify-center border border-[#54E346]/30"
          style={{
            background: 'radial-gradient(circle, rgba(84,227,70,0.15) 0%, rgba(0,0,0,0.8) 100%)',
            boxShadow: '0 0 30px rgba(84,227,70,0.2)',
          }}
          animate={{ boxShadow: ['0 0 20px rgba(84,227,70,0.15)', '0 0 40px rgba(84,227,70,0.3)', '0 0 20px rgba(84,227,70,0.15)'] }}
          transition={{ duration: 2.5, repeat: Infinity }}
        >
          <span className="text-[#54E346] text-[10px] font-mono font-bold tracking-widest">AI</span>
        </motion.div>
      </div>

      {/* Swipe carousel */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
      >
        {FEATURES.map((f, i) => (
          <div key={f.title} className="flex-shrink-0 w-[82vw] snap-center" data-card>
            <motion.div
              className="glass-card rounded-2xl p-6 border border-[#08CB00]/25 relative overflow-hidden
                         hover:border-[#08CB00]/50 active:scale-95 transition-all duration-300 cursor-pointer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              style={{ boxShadow: i === active ? '0 0 20px rgba(8,203,0,0.1)' : 'none' }}
              onClick={() => onSelect(i)}
            >
              <div className="flex items-start justify-between mb-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center text-primary overflow-hidden"
                  style={{ background: `${f.color}15` }}
                >
                  {f.image ? (
                    <div className="w-full h-full bg-center bg-contain bg-no-repeat" style={{ backgroundImage: `url("${encodeURI(f.image)}")` }} />
                  ) : (
                    icons[f.icon]
                  )}
                </div>
                <span className="text-[10px] font-mono text-primary/40">0{i + 1}/0{COUNT}</span>
              </div>

              <h3 className="text-lg font-bold mb-2">{f.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{f.desc}</p>

              {/* Tap hint */}
              <p className="mt-4 text-xs text-primary/40 font-mono">Tap to learn more →</p>
            </motion.div>
          </div>
        ))}
      </div>

      {/* Dots */}
      <div className="flex justify-center gap-2 mt-5">
        {FEATURES.map((_, i) => (
          <button
            key={i}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              i === active ? 'bg-primary w-6' : 'bg-primary/20'
            }`}
            onClick={() => {
              const el = scrollRef.current;
              if (el) {
                const cardW = el.scrollWidth / COUNT;
                el.scrollTo({ left: cardW * i, behavior: 'smooth' });
              }
            }}
            aria-label={`Go to feature ${i + 1}`}
          />
        ))}
      </div>

    </div>
  );
};

/* ═══════════════════════════════════════════════
   TABLET GRID (md only — 2-col between mobile & desktop)
   ═══════════════════════════════════════════════ */
const TabletGrid = ({ onSelect }) => {

  return (
    <>
      <div className="hidden md:grid lg:hidden grid-cols-2 gap-5">
        {FEATURES.map((f, i) => (
          <motion.div
            key={f.title}
            className="glass-card rounded-2xl p-6 border border-[#08CB00]/25 cursor-pointer group
                       hover:border-[#08CB00]/50 transition-all duration-300 relative overflow-hidden"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.07 }}
            whileHover={{ y: -4 }}
            onClick={() => onSelect(i)}
            style={{ boxShadow: 'none' }}
          >
            <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-primary/0 group-hover:bg-primary/5 blur-3xl transition-all duration-500 pointer-events-none" aria-hidden="true" />
            <div className="relative z-10">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-4 group-hover:bg-primary/20 transition-colors overflow-hidden">
                {f.image ? (
                  <div className="w-full h-full bg-center bg-contain bg-no-repeat" style={{ backgroundImage: `url("${encodeURI(f.image)}")` }} />
                ) : (
                  icons[f.icon]
                )}
              </div>
              <h3 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors">{f.title}</h3>
              <p className="text-gray-400 text-base leading-relaxed line-clamp-3">{f.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>

    </>
  );
};

/* ═══════════════════════════════════════════════
   MAIN FEATURES COMPONENT
   ═══════════════════════════════════════════════ */
const Features = () => {
  const [modalIdx, setModalIdx] = useState(null);
  return (
    <>
      <Section id="features" className="py-24 lg:py-32 relative overflow-hidden">
        {/* Starry background */}
        <StarField />
        <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" aria-hidden="true" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <SectionHeading
            tag="Features"
            title={<>Core Capabilities That <span className="gradient-text">Set Us Apart</span></>}
            desc="Six pillars of analytical excellence orbiting our AI core — engineered to transform how your organization harnesses data."
          />

          {/* Desktop orbital */}
          <DesktopOrbit />

          {/* Tablet grid */}
          <TabletGrid onSelect={setModalIdx} />

          {/* Mobile swipeable */}
          <MobileFeatures onSelect={setModalIdx} />
        </div>
      </Section>

      {/* Modal — rendered outside Section to avoid CSS transform stacking context breaking fixed positioning */}
      <AnimatePresence>
        {modalIdx !== null && (
          <DetailModal feature={FEATURES[modalIdx]} onClose={() => setModalIdx(null)} />
        )}
      </AnimatePresence>
    </>
  );
};

export default Features;
